import { Link } from "@tanstack/react-router";

import { FaqList } from "@/components/page-parts";
import type { LocalDepth } from "@/lib/local-conveyancing-depth";

/** Visible in-depth local content block for priority conveyancing pages. */
export function LocalConveyancingDepth({ d }: { d: LocalDepth }) {
  return (
    <section className="mx-auto max-w-4xl px-5 pb-12">
      <div className="rounded-md border-l-4 border-primary bg-card p-6">
        <h2 className="font-serif text-2xl text-foreground">Conveyancing in {d.area}: the short answer</h2>
        <p className="mt-3 text-lg leading-relaxed text-foreground">{d.shortAnswer}</p>
      </div>

      <h2 className="mt-12 font-serif text-3xl text-foreground">What conveyancing costs in {d.area}</h2>
      <div className="mt-5 overflow-x-auto">
        <table className="w-full border-collapse text-left text-base">
          <thead>
            <tr className="border-b border-border">
              <th className="py-2 pr-4 font-semibold text-foreground">Item</th>
              <th className="py-2 pr-4 font-semibold text-foreground">Amount</th>
              <th className="py-2 font-semibold text-foreground">Notes</th>
            </tr>
          </thead>
          <tbody>
            {d.costRows.map((r) => (
              <tr key={r.item} className="border-b border-border">
                <td className="py-3 pr-4 text-foreground">{r.item}</td>
                <td className="py-3 pr-4 font-semibold text-foreground">{r.amount}</td>
                <td className="py-3 text-muted-foreground">{r.note}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-base leading-relaxed text-muted-foreground">
        {d.costNote} Estimate transfer duty with our{" "}
        <Link to="/stamp-duty-calculator-qld" className="text-primary underline">Queensland stamp duty calculator</Link>, or see the{" "}
        <Link to="/fees" className="text-primary underline">full fee schedule</Link>.
      </p>

      <h2 className="mt-12 font-serif text-2xl text-foreground">Before signing: the contract and Form 2</h2>
      <p className="mt-3 text-base leading-relaxed text-muted-foreground">
        The seller disclosure statement is a starting point, not a substitute for checking your
        contract and the property. Send us both documents and tell us whether you are buying a
        home, an investment or a unit, and what you plan to do with it. If you have already signed,
        include the contract date and any finance or building and pest deadlines.
      </p>
      <ul className="mt-4 space-y-3 text-base">
        <li><Link to="/form-2-buyer-rights-qld" className="font-semibold text-primary underline">What to check in a Form 2 and your rights as a buyer</Link></li>
        <li><Link to="/buyers-solicitor-qld" className="font-semibold text-primary underline">Arrange a free contract review before signing</Link></li>
      </ul>

      <h2 className="mt-12 font-serif text-3xl text-foreground">How we help in {d.area}</h2>
      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        {d.transactionTypes.map((t) => (
          <div key={t.t} className="rounded-md border border-border bg-card p-5">
            <h3 className="font-serif text-lg text-foreground">{t.t}</h3>
            <p className="mt-2 text-base leading-relaxed text-muted-foreground">{t.d}</p>
          </div>
        ))}
      </div>

      {d.localSections.map((s) => (
        <div key={s.h}>
          <h2 className="mt-12 font-serif text-2xl text-foreground">{s.h}</h2>
          {s.p.map((p) => (
            <p key={p} className="mt-3 text-base leading-relaxed text-muted-foreground">{p}</p>
          ))}
        </div>
      ))}

      <h2 className="mt-12 font-serif text-2xl text-foreground">Conveyancer or solicitor in {d.area}?</h2>
      {d.conveyancerVsSolicitor.map((p) => (
        <p key={p} className="mt-3 text-base leading-relaxed text-muted-foreground">{p}</p>
      ))}

      <div className="mt-10">
        <FaqList items={d.extraFaqs} />
      </div>

      <p className="mt-8 text-base text-muted-foreground">
        Before you sign, read{" "}
        <Link to="/guides/$slug" params={{ slug: "cooling-off-period-queensland" }} className="text-primary underline">how the Queensland cooling-off period works</Link>{" "}
        and{" "}
        <Link to="/conveyancing-cost-qld" className="text-primary underline">what conveyancing costs in Queensland</Link>.
        {" "}For contract checks, see our{" "}
        <Link to="/buyers-solicitor-qld" className="text-primary underline">buyer's solicitor checklist</Link>,{" "}
        <Link to="/seller-disclosure" className="text-primary underline">seller disclosure advice</Link> and{" "}
        <Link to="/transfer-property-to-family-qld" className="text-primary underline">family property transfer guidance</Link>.
      </p>
    </section>
  );
}
